import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * The last run of an AI tool (its input and result), kept for this tab.
 *
 * Every AI tool hands off to another page (Save as Template, Check Risk,
 * Open Pipeline...). Results lived only in component state, so browser Back
 * returned to an empty tool and the model call had to be paid again. Each
 * tool now keeps its last run in sessionStorage and restores it on mount.
 *
 * A run is stored with the history entry (location.key) it was made on.
 * Back returns to that same entry, router state included, so it restores;
 * a new hand-off (router state on a new entry) brings new input and drops
 * the stored run. Storage that throws (private mode, quota) only means
 * nothing is restored.
 */
const PREFIX = 'comms-v2.ai-run.'

const storage = () => {
    try {
        return window.sessionStorage
    } catch {
        return null
    }
}

/** Drop the tool's stored run (a new run is starting, or new input arrived). */
export const forgetRun = (tool) => {
    try {
        storage()?.removeItem(PREFIX + tool)
    } catch {
        // Nothing stored or storage blocked: nothing to drop.
    }
}

/** Store the tool's run (`data`: whatever the page needs to redraw it) for the entry `locationKey`. */
export const rememberRun = (tool, locationKey, data) => {
    try {
        storage()?.setItem(
            PREFIX + tool,
            JSON.stringify({ key: locationKey, data })
        )
    } catch {
        // Not kept: Back will show an empty tool, as before.
    }
}

/**
 * The tool's stored run to restore at `location`, or null.
 *
 * Null (and the run dropped) when `location` carries router state from a
 * different history entry, because that state is new input for the tool.
 */
export const recallRun = (tool, location) => {
    let saved
    try {
        saved = JSON.parse(storage()?.getItem(PREFIX + tool) ?? 'null')
    } catch {
        return null
    }
    if (!saved || typeof saved !== 'object' || !saved.data) return null
    if (location?.state && saved.key !== location.key) {
        forgetRun(tool)
        return null
    }
    return saved.data
}

/** recall / remember / forget for `tool`, bound to the current history entry. */
export const useToolRun = (tool) => {
    const location = useLocation()
    return useMemo(
        () => ({
            recall: () => recallRun(tool, location),
            remember: (data) => rememberRun(tool, location.key, data),
            forget: () => forgetRun(tool),
        }),
        [tool, location]
    )
}
